import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BankTransaction } from '../../models/bank-transaction';
import { BankTransactionService } from '../../services/bank-transaction.service';
import { Campaign } from '../../models/campaign';
import { CampaignService } from '../../services/campaign.service';
import { RosterService } from '../../services/roster.service';
import { Roster } from '../../models/roster';

@Component({
  selector: 'app-bank',
  imports: [CommonModule, FormsModule],
  templateUrl: './bank.component.html',
  styleUrl: './bank.component.css'
})
export class BankComponent implements OnInit {
  transactions: BankTransaction[] = [];
  filteredTransactions: BankTransaction[] = [];
  expandedDescriptions: boolean[] = [];

  campaigns: Campaign[] = [];
  rosters: Roster[] = [];
  loading = false;

  categories: Record<string, string[]> = {
    'Not Set': [],
    'PaymentIN': [],
    'PaymentOUT': [],
    'Transfer': [],
    'Bills': ['Adobe', 'Azure', 'Google', 'OpenAI', 'Zoom', 'Docusign', 'Otter.AI', 'Calendly', 'LinkedIn', 'Twilio', 'Front'],
    'Fees': ['ACH-Fee', 'Wire-Fee', 'TransferSetup'],
    'Contract Labor': ['Mia', 'Dena', 'Aidan'],
    'Interest': [],
    'Legal Services': [],
    'Office Expenses': [],
    'Taxes and Licenses': [],
    'Travel': ['Airfare', 'Hotel', 'Car Rental', 'Rideshare'],
    'Deductible Meals': ['Client Meal', 'Team Meal'],
    'Other Expenses': ['Flowers', 'Gifts', 'Reimbursement'],
    'Seed Capital': ['Kai', 'Joe']
  };

  descriptionFilter: string = '';
  categoryFilter: string = '';
  campaignFilter: string = '';
  bankFilter: string = '';

  constructor(private bankTxService: BankTransactionService, private campaignService: CampaignService, private rosterService: RosterService) {}

  ngOnInit(): void {
    this.loadTransactions();
    this.loadCampaigns();
    this.loadRosters();
  }

  loadTransactions(): void {
    this.loading = true;
    this.bankTxService.getAll().subscribe({
      next: (items) => {
        this.transactions = items || [];
        this.applyFilter();
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  applyFilter(): void {
    let filtered = this.transactions;

    // Apply bank filter
    if (this.bankFilter.trim()) {
      filtered = filtered.filter(t => t.bank === this.bankFilter);
    }

    // Apply description filter
    if (this.descriptionFilter.trim()) {
      const filterText = this.descriptionFilter.toLowerCase().trim();
      filtered = filtered.filter(t =>
        t.description.toLowerCase().includes(filterText)
      );
    }

    // Apply category filter
    if (this.categoryFilter.trim()) {
      filtered = filtered.filter(t => t.category === this.categoryFilter);
    }

    // Apply campaign filter
    if (this.campaignFilter.trim()) {
      filtered = filtered.filter(t => t.campaign_id === this.campaignFilter);
    }

    this.filteredTransactions = filtered;
    // Reset expanded descriptions when filter changes
    this.expandedDescriptions = new Array(this.filteredTransactions.length).fill(false);
  }

  private mouseDownTime = 0;
  private mouseDownPosition = { x: 0, y: 0 };

  onDescriptionMouseDown(event: MouseEvent, index: number): void {
    this.mouseDownTime = Date.now();
    this.mouseDownPosition = { x: event.clientX, y: event.clientY };
  }

  onDescriptionMouseUp(event: MouseEvent, index: number): void {
    const mouseUpTime = Date.now();
    const timeDiff = mouseUpTime - this.mouseDownTime;
    const distance = Math.sqrt(
      Math.pow(event.clientX - this.mouseDownPosition.x, 2) +
      Math.pow(event.clientY - this.mouseDownPosition.y, 2)
    );

    // Check if user has selected text
    const selection = window.getSelection();
    const hasSelection = selection && selection.toString().length > 0;

    // Only toggle if it was a quick click (< 300ms) with minimal mouse movement (< 5px) and no text selection
    if (timeDiff < 300 && distance < 5 && !hasSelection) {
      this.toggleDescription(index);
    }
  }

  toggleDescription(index: number): void {
    this.expandedDescriptions[index] = !this.expandedDescriptions[index];
  }

  onDescriptionFilterChange(): void {
    this.applyFilter();
  }

  onCategoryFilterChange(): void {
    this.applyFilter();
  }

  onCampaignFilterChange(): void {
    this.applyFilter();
  }

  onBankFilterChange(): void {
    this.applyFilter();
  }

  loadCampaigns(): void {
    this.campaignService.getCampaigns().subscribe({
      next: (campaigns) => { this.campaigns = campaigns || []; },
      error: (err) => { console.error('Failed to load campaigns', err); }
    });
  }

  loadRosters(): void {
    this.rosterService.getRosters().subscribe({
      next: (rosters) => { this.rosters = rosters || []; },
      error: (err) => { console.error('Failed to load rosters', err); }
    });
  }

  getCategoryKeys(): string[] {
    return Object.keys(this.categories);
  }

  get sortedCampaigns(): Campaign[] {
    return this.campaigns
      .filter(campaign => !campaign.deleted)
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  getFilteredTransactionsSum(): number {
    return this.filteredTransactions.reduce((sum, transaction) => sum + transaction.amount, 0);
  }

  getFormattedSummaryAmount(): string {
    const sum = this.getFilteredTransactionsSum();
    if (sum >= 0) {
      return this.formatAmount(sum);
    } else {
      return '(' + this.formatAmount(Math.abs(sum)) + ')';
    }
  }

  onCategoryChange(transaction: BankTransaction, newCategory: string): void {
    // Save the change to the server - server will automatically clear subcategory
    this.bankTxService.patchTransaction(transaction.id, 'category', newCategory).subscribe({
      next: () => {
        // Update successful - local cache already updated by service
        console.log('Transaction category updated successfully');
      },
      error: (err) => {
        console.error('Failed to update transaction category', err);
        // Revert the local change on error
        this.loadTransactions();
      }
    });
  }

  onSubcategoryChange(transaction: BankTransaction, newSubcategory: string): void {
    // Save the subcategory change to the server
    this.bankTxService.patchTransaction(transaction.id, 'subcategory', newSubcategory).subscribe({
      next: () => {
        // Update successful - local cache already updated by service
        console.log('Transaction subcategory updated successfully');
      },
      error: (err) => {
        console.error('Failed to update transaction subcategory', err);
        // Revert the local change on error
        this.loadTransactions();
      }
    });
  }

  getSubcategoriesForCategory(category: string): string[] {
    return this.categories[category] || [];
  }

  hasSubcategories(category: string): boolean {
    const subcategories = this.getSubcategoriesForCategory(category);
    return subcategories.length > 0;
  }

  onCampaignChange(transaction: BankTransaction, newCampaignId: string): void {
    this.bankTxService.patchTransaction(transaction.id, 'campaign_id', newCampaignId).subscribe({
      next: () => {
        // Update successful - local cache already updated by service
        console.log('Transaction campaign updated successfully');
      },
      error: (err) => {
        console.error('Failed to update campaign', err);
        // Revert the change on error
        this.loadTransactions();
      }
    });
  }

  getCampaignName(campaignId: string): string {
    const campaign = this.campaigns.find(c => c.id === campaignId);
    return campaign ? campaign.name : '';
  }

  getCreatorName(creatorId: string): string {
    if (!creatorId) return '';
    const roster = this.rosters.find(r => r.id === creatorId);
    return roster ? roster.name : '';
  }

  formatAmount(amount: number): string {
    const absAmount = Math.abs(amount);
    const formattedAmount = absAmount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    if (amount < 0) {
      return `(${formattedAmount})`;
    } else {
      return formattedAmount;
    }
  }
}
